import { forwardRef } from 'react';
import type { InputHTMLAttributes } from 'react';

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  variant?: 'default' | 'error';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
}

/**
 * Returns mobile-friendly input attributes based on input type.
 * These defaults improve UX on iOS/Android by controlling keyboard behavior.
 * All defaults can be overridden by passing explicit props.
 */
const getTypeDefaults = (type?: string): InputHTMLAttributes<HTMLInputElement> => {
  switch (type) {
    case 'email':
      return {
        autoCapitalize: 'none',
        autoCorrect: 'off',
        spellCheck: false,
        inputMode: 'email',
      };
    case 'url':
      return {
        autoCapitalize: 'none',
        autoCorrect: 'off',
        spellCheck: false,
        inputMode: 'url',
      };
    case 'tel':
      return {
        autoCapitalize: 'none',
        autoCorrect: 'off',
        spellCheck: false,
        inputMode: 'tel',
      };
    case 'search':
      return {
        autoCapitalize: 'none',
        autoCorrect: 'off',
      };
    case 'password':
      return {
        autoCapitalize: 'none',
        autoCorrect: 'off',
        spellCheck: false,
      };
    default:
      // text, number, etc. - use browser defaults
      return {};
  }
};

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ variant = 'default', size = 'md', fullWidth = true, className = '', type, ...props }, ref) => {
    // Get smart defaults for this input type, then spread user props to allow overrides
    const typeDefaults = getTypeDefaults(type);
    const baseClasses = 'border rounded-lg oro-transition focus:oro-focus hover:border-border-hover';
    
    const variantClasses = {
      default: 'border-border bg-surface-alt text-text-primary placeholder:text-text-muted focus:border-accent/60',
      error: 'border-oro-error/60 bg-oro-error/5 text-oro-error placeholder:text-oro-error/70 focus:border-oro-error'
    };

    const sizeClasses = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-4 py-2 text-sm',
      lg: 'px-4 py-3 text-base'
    };

    const widthClass = fullWidth ? 'w-full' : '';

    const classes = `
      ${baseClasses} 
      ${variantClasses[variant]} 
      ${sizeClasses[size]} 
      ${widthClass}
      ${className}
    `.trim();

    return (
      <input
        ref={ref}
        type={type}
        className={classes}
        {...typeDefaults}
        {...props}
      />
    );
  }
);

Input.displayName = 'Input';

export default Input;